/*
 * Search
 */
class SearchModal {

    #debounceTimeoutMs = 325;
    #searchTimeout = null;

    #searchContentEl;
    #searchHeadlineEl;
    #searchResultsEl;
    #searchFieldEl;
    #departmentSearchToggleEl;
    #departmentSearchInputEl;
    #toggleTextEl;

    #modal;

    #defaultHeadline = 'Søg på siden';
    #searchFieldPlaceholder = 'Søg på siden';
    #searchResultHeadline = 'Søgeresultater';
    #toggleStateText = {
        default: "Vis alle søgeresultater",
        withinDepartment: "Vis kun søgeresultater fra afdelingen"
    };
    #currentQuery = '';
    #searchApiUrl = '';
    #isDepartmentSearchSelected;

    constructor(searchApiUrl, isPageAtDepartmentLevel) {
        this.#searchApiUrl = searchApiUrl;
        this.#isDepartmentSearchSelected = isPageAtDepartmentLevel;

        const searchTemplate = `
            <div id="searchContent" class="search-container">
                <div class="search-header">
                    <h2>${this.#defaultHeadline}</h2>
                    <div class="searchbar">
                        <i class="icon icon-search"></i>
                        <input class="search-field"
                            type="search"
                            placeholder="${this.#searchFieldPlaceholder}"
                            tabindex="2"
                            aria-label="${this.#searchFieldPlaceholder}"/>
                    </div>
                </div>
                ${isPageAtDepartmentLevel ?
                `
                <div class="department-search-toggle" tabindex="2">
                    <div class="switch">
                        <input type="checkbox"
                            ${this.#isDepartmentSearchSelected ? `checked` : ``}
                            role="switch"
                            aria-checked=${this.#isDepartmentSearchSelected ? `true` : `false`}
                            aria-describedby="departmentSearchText">
                        <span class="slider"></span>
                    </div>
                    <span id="departmentSearchText" class="text">
                        ${this.#isDepartmentSearchSelected ? this.#toggleStateText.withinDepartment : this.#toggleStateText.default}
                    </span>
                </div>
                ` : ``}
                <div class="search-results">
                </div>
            </div>
        `;

        this.#modal = new Modal(searchTemplate, { initialHeight: isPageAtDepartmentLevel ? '203px' : '150px' })
            .on('opened', () => {
                this.#searchContentEl = document.getElementById('searchContent');
                this.#searchHeadlineEl = this.#searchContentEl.querySelector('.search-header > h2');
                this.#searchResultsEl = this.#searchContentEl.querySelector('.search-results');
                this.#searchFieldEl = this.#searchContentEl.querySelector('.search-field');
                this.#departmentSearchToggleEl = this.#searchContentEl.querySelector(".department-search-toggle");

                if (this.#departmentSearchToggleEl) {
                    this.#departmentSearchInputEl = this.#departmentSearchToggleEl.querySelector("input");
                    this.#toggleTextEl = this.#departmentSearchToggleEl.querySelector(".text");

                    this.#departmentSearchToggleEl.addEventListener('keyup', (e) => {
                        if (e.key === 'Enter' || isPressedKeySpaceBar() ) {
                            this.#controlToggleState();
                        }

                        function isPressedKeySpaceBar() {
                            return e.key === 'Space' || e.keyCode === 32 || e.code === 'Space';
                        }
                    });

                    this.#departmentSearchToggleEl.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.#controlToggleState();
                    });
                }

                this.#searchFieldEl.focus({ focusVisible: true });

                this.#searchFieldEl.addEventListener('keyup', (e) => {
                    if (e.key === 'Enter') {
                        this.#searchForContent();
                    }
                });

                this.#searchFieldEl.addEventListener('input', (e) => this.#debounceSearch());
            });
    }

    show() {
        this.#modal.show();
    }

    on(eventName, func) {
        this.#modal.on(eventName, func);
        return this;
    }

    #controlToggleState() {
        this.#departmentSearchInputEl.setAttribute('aria-checked', !(this.#departmentSearchInputEl.getAttribute('aria-checked') === 'true'));
        this.#departmentSearchInputEl.checked = !this.#departmentSearchInputEl.checked;
        this.#toggleTextEl.innerText = this.#departmentSearchInputEl.checked ? this.#toggleStateText.withinDepartment : this.#toggleStateText.default;
        this.#isDepartmentSearchSelected = this.#departmentSearchInputEl.checked;
        this.#searchForContent({ hasDepartmentSearchToggleStateChanged: true });
    }

    #debounceSearch() {
        if (this.#searchTimeout) {
            clearTimeout(this.#searchTimeout);
        }

        this.#searchTimeout = setTimeout(() => {
            this.#searchTimeout = null;
            this.#searchForContent();
        }, this.#debounceTimeoutMs);
    }

    async #searchForContent(hasDepartmentSearchToggleStateChanged) {
        const query = this.#searchFieldEl.value.trim();

        if (query.length < 3) {
            this.#searchHeadlineEl.innerHTML = this.#defaultHeadline;
            this.#searchResultsEl.innerHTML = '';
            this.#currentQuery = '';
            return;
        }

        if (query === this.#currentQuery && !hasDepartmentSearchToggleStateChanged) return;

        this.#currentQuery = query;

        this.#searchResultsEl.innerHTML = '<span class="loader">Søger</span>';
        this.#modal.expand();

        let loadingAnimationDisplayTime = 350,
            countDown = setInterval(() => {
                loadingAnimationDisplayTime -= 10;

                if (loadingAnimationDisplayTime <= 0) {
                    loadingAnimationDisplayTime = 0;
                    clearInterval(countDown);
                }
            }, 10);

        let searchResults;
        try {
            const response = await fetch(this.#searchApiUrl + `&q=${encodeURIComponent(query)}&ds=${this.#isDepartmentSearchSelected}&_cb=${Date.now()}`, {
                method: 'GET'
            });

            searchResults = await response.text();
            this.#searchHeadlineEl.innerHTML = this.#searchResultHeadline;
        } catch (err) {
            searchResults = err;
        }

        setTimeout(() => {
            this.#searchResultsEl.innerHTML = searchResults;
        }, loadingAnimationDisplayTime);
    }
}

(function () {
    const mainSearchButton = document.getElementById('mainSearchButton'),
        mobileSearchButton = document.getElementById('mobileSearchButton'),
        hiddenSearchButton = document.getElementById('hiddenSearchButton'),
        searchApiUrl = mainSearchButton.dataset.url,
        isPageAtDepartmentLevel = mainSearchButton.hasAttribute('data-department-level');

    const openSearchModal = () => {
        mainSearchButton.disabled = mobileSearchButton.disabled = hiddenSearchButton.disabled = true;

        new SearchModal(searchApiUrl, isPageAtDepartmentLevel)
            .on('closed', () => {
                mainSearchButton.disabled = mobileSearchButton.disabled = hiddenSearchButton.disabled = false;
            })
            .show();
    };

    mainSearchButton.addEventListener('click', () => openSearchModal());
    mobileSearchButton.addEventListener('click', () => openSearchModal());

    hiddenSearchButton.addEventListener('keyup', (event) => {
        if (event.key === 'Enter') {
            hiddenSearchButton.blur();
            openSearchModal();
        }
    });
})();